import {
  last,
  sortBy,
} from 'lodash';
import {
  getMinutes,
  getOffset,
  getDuration,
  convertMinutes,
} from '../utils/TimeUtil';

// content in an event is stored as a time of day, so it cannot run past midnight
const MAX_STOP_TIME = '23:59';

/**
 * Get the latest time content in an event is allowed to stop
 *
 * @param {string} stopTime - time the event stops in H:MM format
 * @returns {string}
 */
export function getMaxStopTime(stopTime) {
  return getMinutes(stopTime) > getMinutes(MAX_STOP_TIME)
    ? MAX_STOP_TIME
    : stopTime;
}

/**
 * Fit the items of a single schedule column inside the event
 *
 * Items stay at the times they are scheduled for and are shortened to the event
 * bounds and to the item before them. Items left without any time inside the
 * event are dropped.
 *
 * @param {array} items - items sharing a column (music, message blocks)
 * @param {string} startTime - time the event starts in H:MM format
 * @param {string} stopTime - time the event stops in H:MM format
 * @returns {array}
 */
export function fitItems({
  items = [],
  startTime,
  stopTime,
}) {
  const eventStartMinutes = getMinutes(startTime);
  const eventStopMinutes = getMinutes(getMaxStopTime(stopTime));
  const fitItemList = [];

  sortBy(items, 'startTime').forEach((item) => {
    const prevItem = last(fitItemList);

    // an item cannot start before the event or encroach on the item before it
    const minStartMinutes = prevItem
      ? Math.max(eventStartMinutes, getMinutes(prevItem.stopTime))
      : eventStartMinutes;

    const itemStartMinutes = Math.max(getMinutes(item.startTime), minStartMinutes);
    const itemStopMinutes = Math.min(getMinutes(item.stopTime), eventStopMinutes);

    if (itemStopMinutes <= itemStartMinutes) {
      // no time is left for the item inside the event
      return;
    }

    const itemStartTime = convertMinutes(itemStartMinutes);
    const itemStopTime = convertMinutes(itemStopMinutes);
    const itemDuration = getDuration(itemStartTime, itemStopTime);

    fitItemList.push({
      ...item,
      startTime: itemStartTime,
      stopTime: itemStopTime,
      offset: getOffset(startTime, itemStartTime),
      duration: itemDuration,
      minutes: getMinutes(itemDuration),
    });
  });

  return fitItemList;
}

/**
 * Fit the interrupts inside the event
 *
 * Interrupts play at a single time instead of over a span, so they cannot be
 * shortened. Any interrupt that no longer plays during the event is dropped.
 *
 * @param {array} interrupts
 * @param {string} startTime - time the event starts in H:MM format
 * @param {string} stopTime - time the event stops in H:MM format
 * @returns {array}
 */
export function fitInterrupts({
  interrupts = [],
  startTime,
  stopTime,
}) {
  const eventStartMinutes = getMinutes(startTime);
  const eventStopMinutes = getMinutes(getMaxStopTime(stopTime));

  return sortBy(interrupts, 'startTime')
    .filter((interrupt) => {
      const interruptStartMinutes = getMinutes(interrupt.startTime);

      return interruptStartMinutes >= eventStartMinutes
        && interruptStartMinutes < eventStopMinutes;
    })
    .map((interrupt) => {
      return {
        ...interrupt,
        offset: getOffset(startTime, interrupt.startTime),
      };
    });
}

export default {
  getMaxStopTime,
  fitItems,
  fitInterrupts,
};
